import { DataRow } from "../interfaces/DataRow";
import { constants } from "../redux/constants";
import { navigate } from "gatsby";
import {
  PAYMENT_METHOD_ALIPAY_QR,
  PAYMENT_METHOD_APPLE_PAY,
  PAYMENT_METHOD_CREDIT_CARD,
  PAYMENT_METHOD_PAYPAL,
  PAYMENT_METHOD_WX_NATIVE,
  PaymentMethodType,
} from "./constants";

/**
 * Goes to new url in the window. Preserves search parameters for usage with partners.
 * Use this to navigate anywhere in the application. Don't use 'href' or some other tag
 * otherwise we lose the search parameters.
 * @param path
 */
export const goTo = (
  path: string,
  state?: object,
  clearLink = false,
  clearPlanId = false
) => {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  let uid = params.get("uid");
  let planid = params.get("planid");

  params.delete("plans");

  if (clearLink || uid) {
    window.history.replaceState({}, document.title, window.location.pathname);
    navigate(path, { state: state });
    return;
  }
  if (clearPlanId && planid) {
    params.set("planid", "");
    navigate(path + "?" + params.toString(), { state: state });
    return;
  }
  const search = params.toString();
  navigate(`${path}${search ? `?${search}` : ""}`, { state });
};

/**
 * Shows data.
 * @param data
 * @returns
 */
export const formatPhoneData = (data: string | number) => {
  // This Function Expect data in GB.
  if (data === "Voice" || Number(data) === 0 || data === "Voice Only") {
    return "Voice";
  }
  if (Number(data) > 9000 || data === "Unlimited") {
    return "Unlimited";
  }
  if (
    typeof data === "string" &&
    (data.indexOf("MB") !== -1 || data.indexOf("GB") !== -1)
  ) {
    return data;
  }
  if (Number(data) < 1) {
    // < 1 GB
    return (Number(data) * 1024).toFixed(0) + " MB";
  }
  return data + " GB";
};

/**
 * Gets carrier full name.
 * @param short
 * @returns
 */
export const getCarrierFullName = (short: string) => {
  if (short === "T") {
    return "Telus";
  } else {
    return "Rogers";
  }
};

/**
 * Maps plan data from phonebox API only.
 * @param data
 * @param planType
 * @returns
 */
export const mapBoxOnePlans = (data: any, planType: string): DataRow[] => {
  const isPostPaid = planType === constants.POSTPAID_PLAN;
  let plans: DataRow[] = data.map((e) => {
    return {
      planId: isPostPaid ? e.pfId : e.PlanId,
      plan_fee: isPostPaid ? e.pfAmt : e.Fee,
      carrier: e.telusplan
        ? "Telus"
        : e.Carriers
        ? getCarrierFullName(e.Carriers)
        : getCarrierFullName(""),
      pfCode: isPostPaid ? e.pfCode : e.PlanCode,
      data: isPostPaid
        ? formatPhoneData(e.dataCap.split(".")[0])
        : formatPhoneData((e.Data / (1024 * 1024)).toString()),
      planTypeD: isPostPaid ? "" : e.PlanTypeD,
      calling: e.Calling ? e.Calling : null, // Applies for data-only plans.  "N/A" means data only.
      message: e.Message ? e.Message : null, // Applies for data-only plans.  "N/A" means data only.
      plintronPlanId: null, // Applies to Portaone Plans only. Doesn't apply to BoxOne plans but we need to maintain the plans structure to avoid undefined errors
      subscriptionId: null, // Applies to Portaone Plans only. Doesn't apply to BoxOne plans but we need to maintain the plans structure to avoid undefined errors
    } as DataRow;
  });
  return plans;
};

/**
 * Maps a boolean value to its integer value. Portaone APIs use ints for bool. While we likely don't
 * need to write something like this it will make it more clear looking at the code why
 * we're using using 1's or 0's for an object property when calling the proxy api
 * on a property that looks like a boolean.
 */
export const fromBooleanToNumber = (value: boolean): number => {
  return value ? 1 : 0;
};

/**
 * Gets the partner id (for affiliates) from the URL parameters of the page location.
 * @param location
 * @returns
 */
export const getPartnerIdFromPageLocation = (
  location: any
): { canadaPartnerId: number; usPartnerId: number } => {
  var url = new URL(location.href);
  let canadaPartnerId = url.searchParams.get("bn");
  const usPartnerId = url.searchParams.get("i_rep");
  // check local storage for rbc affiliate link
  let hasRbcLink = sessionStorage.getItem("raLink");
  if (hasRbcLink === "true" && !canadaPartnerId) {
    canadaPartnerId = "4036";
  }
  return {
    canadaPartnerId: !!canadaPartnerId ? +canadaPartnerId : 0,
    usPartnerId: !!usPartnerId ? +usPartnerId : 0,
  };
};

/**
 * Returns strings with only numbers in it.
 * @param value
 * @returns
 */
export const numbersOnly = (value) => value.replace(/\D/g, "");

/**
 * Parses a string to return only characters valid for a phone number.
 * @param value
 * @returns
 */
export const phoneNumbersOnly = (value) => value.replace(/[^0-9+\s-]/g, "");

/**
 * Sends a request to the specified url from a form. This will change the window location.
 * @param {string} path the path to send the post request to
 * @param {object} params the parameters to add to the url
 * @param {string} [method=post] the method to use on the form
 */

export const submitAsForm = (path, params, method = "post") => {
  // The rest of this code assumes you are not using a library.
  // It can be made less verbose if you use one.
  const form = document.createElement("form");
  form.method = method;
  form.action = path;
  for (const key in params) {
    if (params.hasOwnProperty(key)) {
      const hiddenField = document.createElement("input");
      hiddenField.type = "hidden";
      hiddenField.name = key;
      hiddenField.value = params[key];
      form.appendChild(hiddenField);
    }
  }
  document.body.appendChild(form);
  form.submit();
};

/**
 * Gets the payment_method from the from the URL parameters of the page location for US portaone purchases via PayPal.
 * @param location
 * @returns
 */
export const getPaymentMethodFromUrl = (location: any): string => {
  var url = new URL(location.href);
  const paymentId = url.searchParams.get("payment_method") || null;
  return paymentId;
};

/**
 * Maps string constant of payment method to enumeration used in Proxy API.
 * @param paymentMethod
 * @returns
 */
export const getPaymentMethodType = (
  paymentMethod: string
): PaymentMethodType => {
  switch (paymentMethod) {
    case PAYMENT_METHOD_CREDIT_CARD:
      return PaymentMethodType.CreditCard;
    case PAYMENT_METHOD_PAYPAL:
      return PaymentMethodType.PayPal;
    case PAYMENT_METHOD_ALIPAY_QR:
      return PaymentMethodType.AliPayQR;
    case PAYMENT_METHOD_APPLE_PAY:
      return PaymentMethodType.ApplePay;
    case PAYMENT_METHOD_WX_NATIVE:
      return PaymentMethodType.WxNative;
    case PAYMENT_METHOD_ALIPAY_QR:
      return PaymentMethodType.AliPayQR;
  }
};

/**
 * Returns an encrypted string of the promocode based on Joe's needs
 * @param promocode
 * @returns encrypted string
 */
export const encryptPromocode = (promocode: string) => {
  let seedkey = "a7a3428a16a0";

  const textEncoder = new TextEncoder();
  let bytes = textEncoder.encode(promocode);
  let key = textEncoder.encode(seedkey);
  let encryptedBytes = new Uint8Array(bytes.length);

  for (let i = 0; i < bytes.length; i++) {
    encryptedBytes[i] = bytes[i] ^ key[i % key.length];
  }

  let base64string = btoa(String.fromCharCode.apply(null, encryptedBytes));
  return base64string;
};

/**
 * Returns the proper label depending on planTypeD
 * @param days
 * @returns string indicating timespan
 */
export const getTimeFromPlanTypeD = (days: string, prepaid?: boolean) => {
  if (prepaid) {
    return days + " days";
  }

  switch (days) {
    case "7":
      return "week";
    case "30":
      return "month";
    case "90":
      return "90 days";
    default:
      return "month";
  }
};
export const isTestEnv = (): boolean => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host.includes("localhost") || host.includes("azurestaticapps.net");
};
export const devLog = (...args: any[]) => {
  if (typeof window === "undefined") return;
  if (isTestEnv()) {
    console.log("🔍", ...args);
  }
};
/**
 * Navigates to /plans with preserved query parameters (e.g., bn, i_rep),
 * This also respects language paths like /jp/plans.
 *
 * @param plan "us" | "canada"
 */
export const goToPlansWithParams = (rawCountry: string) => {
  const countryCodeMap: Record<string, "us" | "ca"> = {
    usa: "us",
    us: "us",
    canada: "ca",
    ca: "ca",
  };

  const plan = countryCodeMap[rawCountry.toLowerCase()] || "ca";

  const currentUrl = new URL(window.location.href);
  const currentParams = new URLSearchParams(currentUrl.search);

  // Remove params that will cause auto-redirects
  ["plans", "planid", "type"].forEach((key) => currentParams.delete(key));
  currentParams.set("plans", plan);

  const pathParts = currentUrl.pathname.split("/").filter(Boolean);
  const langCodes = ["en", "fr", "jp", "ko", "pt", "sp", "vi", "zh"];
  const langPrefix = langCodes.includes(pathParts[0]) ? `/${pathParts[0]}` : "";

  const newUrl = `${langPrefix}/plans?${currentParams.toString()}`;
  window.open(newUrl, "_self");
};
